import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom'
import {
  ActionButtons,
  EmptyState,
  SearchWithFilter,
} from '@enamelai/design-system'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import EditSavedViewModal, {
  type EditSavedViewValues,
} from '@components/pages/Reports/shared/EditSavedViewModal.component'
import EditScheduledReportModal, {
  type EditScheduledReportValues,
} from '@components/pages/Reports/shared/EditScheduledReportModal.component'
import ReportDataTable, {
  ReportDataTableColumn,
} from '@components/pages/Reports/shared/ReportDataTable.component'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  CustomReportRow,
  GeneratedReportRow,
  PRESETS_AUTOMATION_TABS,
  PRESETS_CUSTOM_REPORTS_MOCK,
  PRESETS_GENERATED_REPORTS_MOCK,
  PRESETS_SCHEDULED_REPORTS_MOCK,
  PRESETS_SAVED_VIEWS_MOCK,
  PresetsAutomationTabId,
  PRESETS_TAB_QUERY,
  type PresetsReturnState,
  SavedViewRow,
  ScheduledReportRow,
  isPresetCustomReportRowId,
  isPresetSavedViewRowId,
  isPresetScheduledReportRowId,
} from '@constants/Reports/presetsAutomation.constants'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  removeCustomReport,
  removeSavedView,
  removeScheduledReport,
  updateSavedView,
  updateScheduledReport,
} from '@store/actions/reports.action'
import type { ReportsOutletContext } from '@pages/Reports/Reports.page'
import ReportsSkeleton from './ReportsSkeleton.page'
import { toSentenceCase } from '@utils/textCase.util'

const PRESETS_SKELETON_DURATION_MS = 500

type FilterableRow = Pick<
  SavedViewRow,
  'name' | 'dateSaved' | 'lastUpdated' | 'savedBy'
>

function parsePresetListDate(s: string): number {
  const t = Date.parse(s)
  return Number.isNaN(t) ? 0 : t
}

/** Newest / most recently updated first (Presets tables). */
function sortByLastUpdatedDesc<T extends { lastUpdated: string }>(
  rows: T[],
): T[] {
  return [...rows].sort(
    (a, b) =>
      parsePresetListDate(b.lastUpdated) - parsePresetListDate(a.lastUpdated),
  )
}

function filterRows<TRow extends FilterableRow>(
  rows: TRow[],
  query: string,
  savedByFilters: string[],
): TRow[] {
  const q = query.trim().toLowerCase()
  return rows.filter((row) => {
    const matchesQuery =
      q.length === 0 ||
      row.name.toLowerCase().includes(q) ||
      row.savedBy.toLowerCase().includes(q) ||
      row.dateSaved.toLowerCase().includes(q) ||
      row.lastUpdated.toLowerCase().includes(q)
    const matchesSavedBy =
      savedByFilters.length === 0 || savedByFilters.includes(row.savedBy)
    return matchesQuery && matchesSavedBy
  })
}

function filterScheduledRows(
  rows: ScheduledReportRow[],
  query: string,
  savedByFilters: string[],
): ScheduledReportRow[] {
  const q = query.trim().toLowerCase()
  return rows.filter((row) => {
    const dateAdded = row.dateAdded ?? ''
    const schedLabel = row.scheduled ? 'yes' : 'no'
    const matchesQuery =
      q.length === 0 ||
      row.name.toLowerCase().includes(q) ||
      row.summaryLine.toLowerCase().includes(q) ||
      row.savedBy.toLowerCase().includes(q) ||
      row.dateSaved.toLowerCase().includes(q) ||
      row.lastUpdated.toLowerCase().includes(q) ||
      row.scheduleDate.toLowerCase().includes(q) ||
      dateAdded.toLowerCase().includes(q) ||
      schedLabel.includes(q)
    const matchesSavedBy =
      savedByFilters.length === 0 || savedByFilters.includes(row.savedBy)
    return matchesQuery && matchesSavedBy
  })
}

function PresetsAutomationPage(): React.ReactElement {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isPageReady } = useOutletContext<ReportsOutletContext>()
  const [isPresetsSkeleton, setIsPresetsSkeleton] = useState(false)
  const [presetsSkeletonKey, setPresetsSkeletonKey] = useState(0)
  const presetsTabKey = searchParams.get('tab') ?? 'saved'

  useEffect(() => {
    if (!isPageReady) return
    setIsPresetsSkeleton(true)
    setPresetsSkeletonKey((key) => key + 1)
  }, [presetsTabKey, isPageReady])

  useEffect(() => {
    if (!isPresetsSkeleton) return
    const timer = window.setTimeout(() => {
      setIsPresetsSkeleton(false)
    }, PRESETS_SKELETON_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [isPresetsSkeleton, presetsSkeletonKey])
  const customReports = useAppSelector((state) => state.reports.customReports)
  const scheduledReports = useAppSelector(
    (state) => state.reports.scheduledReports,
  )
  const userSavedViews = useAppSelector((state) => state.reports.savedViews)

  const [activeTab, setActiveTab] =
    useState<PresetsAutomationTabId>('savedViews')

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'scheduled' || tab === 'scheduledReports') {
      setActiveTab('scheduledReports')
    } else if (tab === 'customized' || tab === 'customizedReports') {
      setActiveTab('customizedReports')
    } else if (tab === 'generated' || tab === 'generatedReports') {
      setActiveTab('generatedReports')
    } else if (tab === 'saved' || tab === 'savedViews') {
      setActiveTab('savedViews')
    }
  }, [searchParams])
  const { showToast } = useNotificationToast()
  const [searchQuery, setSearchQuery] = useState('')
  const [savedByFilters, setSavedByFilters] = useState<string[]>([])
  const [generatedReports, setGeneratedReports] = useState<
    GeneratedReportRow[]
  >(() => [...PRESETS_GENERATED_REPORTS_MOCK])
  const [editSavedView, setEditSavedView] = useState<SavedViewRow | null>(null)
  const [editScheduledReport, setEditScheduledReport] =
    useState<ScheduledReportRow | null>(null)

  const allScheduledRows = useMemo<ScheduledReportRow[]>(() => {
    const overridesById = new Map(scheduledReports.map((r) => [r.id, r]))
    const mergedMocks = sortByLastUpdatedDesc(
      PRESETS_SCHEDULED_REPORTS_MOCK.map(
        (row) => overridesById.get(row.id) ?? row,
      ),
    )
    const userOnly = scheduledReports.filter(
      (row) =>
        !PRESETS_SCHEDULED_REPORTS_MOCK.some((mock) => mock.id === row.id),
    )
    return [...userOnly, ...mergedMocks]
  }, [scheduledReports])

  const allCustomReportRows = useMemo<CustomReportRow[]>(
    () => [
      ...customReports,
      ...sortByLastUpdatedDesc(PRESETS_CUSTOM_REPORTS_MOCK),
    ],
    [customReports],
  )

  const allSavedViewRows = useMemo<SavedViewRow[]>(() => {
    const overridesById = new Map(userSavedViews.map((r) => [r.id, r]))
    const mergedMocks = sortByLastUpdatedDesc(
      PRESETS_SAVED_VIEWS_MOCK.map((row) => overridesById.get(row.id) ?? row),
    )
    const userOnly = userSavedViews.filter(
      (row) => !PRESETS_SAVED_VIEWS_MOCK.some((mock) => mock.id === row.id),
    )
    return [...userOnly, ...mergedMocks]
  }, [userSavedViews])

  const handleSaveEditedSavedView = useCallback(
    (values: EditSavedViewValues): void => {
      if (!editSavedView) return
      const nextToolbar = { ...values.toolbarSelection }
      const updated: SavedViewRow = {
        ...editSavedView,
        name: values.name,
        preset: {
          ...editSavedView.preset,
          toolbarSelection: nextToolbar,
        },
        bundleView: editSavedView.bundleView
          ? {
              ...editSavedView.bundleView,
              toolbarSelection: {
                ...editSavedView.bundleView.toolbarSelection,
                ...nextToolbar,
              },
            }
          : undefined,
      }
      void dispatch(updateSavedView(updated))
      showToast(
        createSuccessToast('Saved view updated', `${values.name} was saved.`),
      )
      setEditSavedView(null)
    },
    [dispatch, editSavedView, showToast],
  )

  const editScheduledInitialToolbar = useMemo(() => {
    if (!editScheduledReport) return undefined
    if (editScheduledReport.toolbarSelection) {
      return editScheduledReport.toolbarSelection
    }
    const target = editScheduledReport.viewTarget
    if (target?.kind !== 'savedView') return undefined
    const saved =
      allSavedViewRows.find((r) => r.id === target.savedViewId) ??
      PRESETS_SAVED_VIEWS_MOCK.find((r) => r.id === target.savedViewId)
    if (!saved) return undefined
    return {
      ...saved.preset.toolbarSelection,
      ...(saved.bundleView?.toolbarSelection ?? {}),
    }
  }, [allSavedViewRows, editScheduledReport])

  const handleSaveEditedScheduledReport = useCallback(
    (values: EditScheduledReportValues): void => {
      if (!editScheduledReport) return
      const updated: ScheduledReportRow = {
        ...editScheduledReport,
        name: values.name,
        summaryLine: values.summaryLine,
        toolbarSelection: values.toolbarSelection,
        scheduled: true,
      }
      void dispatch(updateScheduledReport(updated))
      showToast(
        createSuccessToast(
          'Scheduled report updated',
          `${values.name} was saved.`,
        ),
      )
      setEditScheduledReport(null)
    },
    [dispatch, editScheduledReport, showToast],
  )

  const savedByOptions = useMemo<string[]>(() => {
    const set = new Set<string>()
    if (activeTab === 'customizedReports') {
      allCustomReportRows.forEach((r) => set.add(r.savedBy))
    } else if (activeTab === 'scheduledReports') {
      allScheduledRows.forEach((r) => set.add(r.savedBy))
    } else if (activeTab === 'generatedReports') {
      generatedReports.forEach((r) => set.add(r.savedBy))
    } else {
      allSavedViewRows.forEach((r) => set.add(r.savedBy))
    }
    return Array.from(set)
  }, [
    activeTab,
    allCustomReportRows,
    allSavedViewRows,
    allScheduledRows,
    generatedReports,
  ])

  const filteredSavedViews = useMemo(
    () => filterRows(allSavedViewRows, searchQuery, savedByFilters),
    [allSavedViewRows, searchQuery, savedByFilters],
  )

  const filteredCustomReports = useMemo(
    () => filterRows(allCustomReportRows, searchQuery, savedByFilters),
    [allCustomReportRows, searchQuery, savedByFilters],
  )

  const filteredScheduledReports = useMemo(
    () => filterScheduledRows(allScheduledRows, searchQuery, savedByFilters),
    [allScheduledRows, searchQuery, savedByFilters],
  )

  const filteredGeneratedReports = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return generatedReports.filter((row) => {
      const matchesQuery =
        q.length === 0 ||
        row.name.toLowerCase().includes(q) ||
        row.dateGenerated.toLowerCase().includes(q) ||
        row.schedule.toLowerCase().includes(q) ||
        row.savedBy.toLowerCase().includes(q)
      const matchesSavedBy =
        savedByFilters.length === 0 || savedByFilters.includes(row.savedBy)
      return matchesQuery && matchesSavedBy
    })
  }, [generatedReports, searchQuery, savedByFilters])

  const savedViewColumns: ReportDataTableColumn<SavedViewRow>[] = useMemo(
    () => [
      { key: 'name', label: 'Saved Views' },
      { key: 'dateSaved', label: 'Date Saved' },
      { key: 'lastUpdated', label: 'Last Updated' },
      { key: 'savedBy', label: 'Saved By' },
      {
        key: 'actions',
        label: 'Actions',
        headerClassName: 'reports-presets-actions-col',
        cellClassName: 'reports-presets-actions-col',
        render: (row) => {
          const preset = isPresetSavedViewRowId(row.id)
          return (
            <ActionButtons
              showView={false}
              onEdit={() => setEditSavedView(row)}
              editVariant="outline"
              showDelete
              deleteVariant="outline"
              onDelete={() => {
                if (preset) {
                  showToast(
                    createFailedToast(
                      'Cannot remove',
                      'Sample saved views are built in for the demo.',
                    ),
                  )
                  return
                }
                void dispatch(removeSavedView(row.id))
              }}
            />
          )
        },
      },
    ],
    [dispatch, showToast],
  )

  const customReportColumns: ReportDataTableColumn<CustomReportRow>[] = useMemo(
    () => [
      { key: 'name', label: 'Reports' },
      { key: 'dateSaved', label: 'Date Saved' },
      { key: 'lastUpdated', label: 'Last Updated' },
      { key: 'savedBy', label: 'Saved By' },
      {
        key: 'actions',
        label: 'Actions',
        headerClassName: 'reports-presets-actions-col',
        cellClassName: 'reports-presets-actions-col',
        render: (row) => {
          const preset = isPresetCustomReportRowId(row.id)
          return (
            <ActionButtons
              showView={false}
              onEdit={() =>
                navigate(`/report/custom/${row.id}`, {
                  state: {
                    presetsReturnTab: 'customizedReports',
                  } satisfies PresetsReturnState,
                })
              }
              editVariant="outline"
              showDelete
              deleteVariant="outline"
              onDelete={() => {
                if (preset) {
                  showToast(
                    createFailedToast(
                      'Cannot remove',
                      'Sample custom reports are built in for the demo.',
                    ),
                  )
                  return
                }
                void dispatch(removeCustomReport(row.id))
              }}
            />
          )
        },
      },
    ],
    [dispatch, navigate, showToast],
  )

  const scheduledReportColumns: ReportDataTableColumn<ScheduledReportRow>[] =
    useMemo(
      () => [
        { key: 'name', label: 'Reports' },
        { key: 'dateSaved', label: 'Date Saved' },
        { key: 'lastUpdated', label: 'Last Updated' },
        { key: 'savedBy', label: 'Saved By' },
        {
          key: 'scheduled',
          label: 'Scheduled',
          render: (row) => (row.scheduled ? 'Yes' : 'No'),
        },
        { key: 'scheduleDate', label: 'Schedule Date' },
        {
          key: 'actions',
          label: 'Actions',
          headerClassName: 'reports-presets-actions-col',
          cellClassName: 'reports-presets-actions-col',
          render: (row) => {
            const preset = isPresetScheduledReportRowId(row.id)
            return (
              <ActionButtons
                showView={false}
                onEdit={() => setEditScheduledReport(row)}
                editVariant="outline"
                showDelete
                deleteVariant="outline"
                onDelete={() => {
                  if (preset) {
                    showToast(
                      createFailedToast(
                        'Cannot remove',
                        'Sample scheduled reports are built in for the demo.',
                      ),
                    )
                    return
                  }
                  void dispatch(removeScheduledReport(row.id))
                }}
              />
            )
          },
        },
      ],
      [dispatch, showToast],
    )

  const generatedReportColumns: ReportDataTableColumn<GeneratedReportRow>[] =
    useMemo(
      () => [
        { key: 'name', label: 'Reports' },
        { key: 'dateGenerated', label: 'Date generated' },
        { key: 'schedule', label: 'Schedule' },
        {
          key: 'actions',
          label: 'Actions',
          headerClassName: 'reports-presets-actions-col',
          cellClassName: 'reports-presets-actions-col',
          render: (row) => {
            return (
              <ActionButtons
                showView={false}
                onEdit={() => {
                  const customReportId = row.id.replace(
                    /^preset-gen-/,
                    'preset-cr-',
                  )
                  navigate(`/report/custom/${customReportId}`, {
                    state: {
                      presetsReturnTab: 'generatedReports',
                    } satisfies PresetsReturnState,
                  })
                }}
                editVariant="outline"
                showDelete
                deleteVariant="outline"
                onDelete={() => {
                  setGeneratedReports((prev) =>
                    prev.filter((r) => r.id !== row.id),
                  )
                  showToast(
                    createSuccessToast(
                      'Report removed',
                      `${row.name} was deleted.`,
                    ),
                  )
                }}
              />
            )
          },
        },
      ],
      [navigate, showToast],
    )

  const handleFilterChange = (next: Record<string, string[]>): void => {
    setSavedByFilters(next.savedBy ?? [])
  }

  const handleTabChange = (value: string): void => {
    const next = value as PresetsAutomationTabId
    setActiveTab(next)
    setSavedByFilters([])
    setSearchParams({ tab: PRESETS_TAB_QUERY[next] }, { replace: true })
  }

  const renderTabEmptyState = (hasSourceRows: boolean): React.ReactElement => (
    <EmptyState
      title={hasSourceRows ? 'No matching reports' : 'No reports yet'}
      description={
        hasSourceRows
          ? 'No results match your search or filters.'
          : 'Saved and generated reports will appear here.'
      }
      className="dashboard-section-empty--embed"
    />
  )

  return isPresetsSkeleton ? (
    <ReportsSkeleton variant="presets" />
  ) : (
    <div className="reports-main-panel reports-presets-panel">
      <div className="reports-presets-toolbar">
        <ReportViewTabs
          options={PRESETS_AUTOMATION_TABS.map((t) => ({
            label: toSentenceCase(t.label),
            value: t.id,
          }))}
          value={activeTab}
          onChange={handleTabChange}
        />
        <SearchWithFilter
          className="reports-presets-swf"
          placeholder="Search"
          value={searchQuery}
          onChange={setSearchQuery}
          filters={{ savedBy: savedByOptions }}
          onFilterChange={handleFilterChange}
          filterLabels={{ savedBy: 'Saved by' }}
        />
      </div>

      <div className="reports-presets-content">
        {activeTab === 'savedViews' ? (
          filteredSavedViews.length === 0 ? (
            renderTabEmptyState(allSavedViewRows.length > 0)
          ) : (
            <ReportDataTable
              columns={savedViewColumns}
              rows={filteredSavedViews}
              rowKey={(row) => row.id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialPageSize={10}
              className="reports-presets-table"
              sortIndicatorVariant="dashboard"
            />
          )
        ) : activeTab === 'scheduledReports' ? (
          filteredScheduledReports.length === 0 ? (
            renderTabEmptyState(allScheduledRows.length > 0)
          ) : (
            <ReportDataTable
              columns={scheduledReportColumns}
              rows={filteredScheduledReports}
              rowKey={(row) => row.id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialPageSize={10}
              className="reports-presets-table"
              sortIndicatorVariant="dashboard"
            />
          )
        ) : activeTab === 'customizedReports' ? (
          filteredCustomReports.length === 0 ? (
            renderTabEmptyState(allCustomReportRows.length > 0)
          ) : (
            <ReportDataTable
              columns={customReportColumns}
              rows={filteredCustomReports}
              rowKey={(row) => row.id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialPageSize={10}
              className="reports-presets-table"
              sortIndicatorVariant="dashboard"
            />
          )
        ) : filteredGeneratedReports.length === 0 ? (
          renderTabEmptyState(generatedReports.length > 0)
        ) : (
          <ReportDataTable
            columns={generatedReportColumns}
            rows={filteredGeneratedReports}
            rowKey={(row) => row.id}
            pageSizeOptions={[10, 25, 50, 100]}
            initialPageSize={10}
            className="reports-presets-table"
            sortIndicatorVariant="dashboard"
          />
        )}
      </div>

      <EditSavedViewModal
        show={editSavedView !== null}
        savedView={editSavedView}
        onHide={() => setEditSavedView(null)}
        onSave={handleSaveEditedSavedView}
      />
      <EditScheduledReportModal
        show={editScheduledReport !== null}
        scheduledReport={editScheduledReport}
        initialToolbarSelection={editScheduledInitialToolbar}
        onHide={() => setEditScheduledReport(null)}
        onSave={handleSaveEditedScheduledReport}
      />
    </div>
  )
}

export default PresetsAutomationPage
